import { aggregateMetricsData } from 'src/utilities/CommonUtilities';
import { logger } from 'src/logger';
import { TicketsParsedDataItem } from '../../tickets/TicketsInterfaces';
import {
  prepareTicketDataForAggregation,
  mapBaseMetricsToAggregatedData,
  calculateMonthTotals,
  calculateApplicationTotals,
  aggregateResolutionTypeCounts
} from './BusinessReportingTicketsAggregatorUtils';
import {
  BUSINESS_REPORTING_TICKET_COLUMN_LIST,
  BUSINESS_REPORTING_TICKET_MEASURE_COLUMN_LIST,
  BUSINESS_REPORTING_TOTALS_MEASURE_COLUMNS,
  createEmptyBusinessReportingAggregatedData
} from '../BusinessReportingConstants';
import { BusinessReportingAggregatedResult } from '../BusinessReportingInterfaces';

/**
 * Main function to aggregate all business reporting ticket data
 * @param filteredTicketData - Pre-filtered ticket data (filtering now done centrally)
 * @param filters - Business reporting filters
 * @returns Complete aggregated business reporting data per month per application
 */
export const aggregateBusinessReportingData = (filteredTicketData: TicketsParsedDataItem[]): BusinessReportingAggregatedResult => {
  try {
    if (filteredTicketData.length === 0) {
      logger.info('No filtered ticket data available for aggregation');
      return createEmptyBusinessReportingAggregatedData();
    }

    const preparedData = prepareTicketDataForAggregation(filteredTicketData);

    const baseMetrics = aggregateMetricsData(BUSINESS_REPORTING_TICKET_COLUMN_LIST, preparedData, BUSINESS_REPORTING_TICKET_MEASURE_COLUMN_LIST);

    if (!baseMetrics || baseMetrics.length === 0) {
      logger.warn('No aggregated data returned from aggregateMetricsData');
      return createEmptyBusinessReportingAggregatedData();
    }

    const { resolutionTypeMap, sev2ResolutionTypeMap } = aggregateResolutionTypeCounts(filteredTicketData);

    // Merge resolution type data with base metrics
    const finalAggregatedData = mapBaseMetricsToAggregatedData(baseMetrics, resolutionTypeMap, sev2ResolutionTypeMap);

    // Calculate totals using aggregateMetricsData
    const totalsAggregated = aggregateMetricsData(
      [], // Empty columnList - no grouping, single result
      finalAggregatedData,
      BUSINESS_REPORTING_TOTALS_MEASURE_COLUMNS
    );

    const totals = totalsAggregated && totalsAggregated.length > 0 ? totalsAggregated[0] : createEmptyBusinessReportingAggregatedData().totals || {};

    let monthTotals = calculateMonthTotals(finalAggregatedData);
    let applicationTotals = calculateApplicationTotals(finalAggregatedData);

    return {
      entries: finalAggregatedData,
      totalTimeSpentSum: Math.ceil(totals.total_time_spent || 0),
      totals: totals,
      monthTotals: monthTotals,
      applicationTotals: applicationTotals
    };
  } catch (error) {
    logger.error('Error in main business reporting data aggregation', error as Error);
    return createEmptyBusinessReportingAggregatedData();
  }
};
